import React from 'react'
import styled, { CSSObject } from '@emotion/styled'
import {
  AdditionalItems,
  LoyaltyCard,
  OrderItem,
  PotentialLoyaltyGains as PotentialLoyaltyGainsType,
  Summary
} from 'shop/types/cart'
import CartTitle from './CartTitle/CartTitle'
import { CartItemList } from './CartItems'
import CartBreakdown from './CartBreakdown/CartBreakdown'
import NoteBreakdownItem from './CartBreakdown/NoteBreakdownItem'
import CartBreakdownItem from './CartBreakdown/CartBreakdownItem'
import { useEffect, useLayoutEffect, useState } from 'react'
import { ExpandableContainer } from '../ExpandableContainer'
import isEmpty from 'lodash/isEmpty'
import { IoCreateOutline } from 'react-icons/io5'
import { AiOutlineGift as Gift } from 'react-icons/ai'
import { FulfillmentType } from 'shop/types'
import { LineBreak } from '../Shop/commonStyles'
import { StyledHTMLElement } from 'shop/theme/types'
import { useCheckout } from 'shop/hooks'
import { AlcoholNote } from './AlcoholNote'

/** Shop refers to the version of the Cart shown on the Shop page (on page or cart modal on mobile)
 * and the Summary refers to the version shown on the Checkout, Payment and Order Confirmation pages */
export enum CartType {
  Shop = 'Shop',
  Summary = 'Summary'
}

interface Props {
  type: CartType
  orderItems: OrderItem[]
  summary: Summary
  loyaltyCards?: LoyaltyCard[]
  orderNotes: string | null
  additionalItems: AdditionalItems
  fulfillmentType: FulfillmentType
  potentialLoyaltyGains?: PotentialLoyaltyGainsType | null
  reference?: string
  isEditable?: boolean
  isCartLoading?: boolean
}

const Cart = ({
  type,
  orderItems,
  summary,
  loyaltyCards,
  orderNotes,
  additionalItems,
  fulfillmentType,
  potentialLoyaltyGains,
  reference,
  isEditable = false,
  isCartLoading = false
}: Props) => {
  const ref: React.RefObject<HTMLDivElement> = React.createRef()
  const [hasExpanded, setHasExpanded] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number | undefined>()
  const [isSeeMoreEnabled, setIsSeeMoreEnabled] = useState(false)

  // TODO: Make this a prop and pass it down from parent component (wait for JFE checkout work to be done)
  const { hasAlcoholicProducts } = useCheckout()

  useEffect(() => {
    // for animation purposes, setting the maxHeight to fit the content
    setMaxHeight(ref.current?.scrollHeight)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current?.scrollHeight, setMaxHeight])

  // Check child ref height to see if we need to show the "See more" or not
  useLayoutEffect(() => {
    if (ref.current) {
      if (ref.current?.clientHeight < ref.current.scrollHeight) {
        setIsSeeMoreEnabled(true)
      }
    }
  }, [ref])

  const handleSeeMoreClick = () => setHasExpanded(!hasExpanded)

  const { giftWrapping } = additionalItems
  const showGiftNote = !!(giftWrapping?.message && giftWrapping?.price)

  return (
    <div data-testid='cart-overview'>
      <CartTitle type={type} reference={reference} />
      {!!hasAlcoholicProducts && (
        <>
          <Section margin='4px 0 16px'>
            <AlcoholNote />
          </Section>
          <LineBreak height='2px' />
        </>
      )}
      {!!orderNotes && (
        <NoteBreakdownItemContainer>
          <NoteBreakdownItem
            Icon={IoCreateOutline}
            noteTitle='Note'
            note={orderNotes}
          />
        </NoteBreakdownItemContainer>
      )}
      <Section margin={isSeeMoreEnabled ? '16px 0px 16px' : '16px 0px 0px'}>
        <ExpandableContainer
          {...{
            hasExpanded,
            isSeeMoreEnabled,
            maxHeight,
            handleSeeMoreClick,
            containerRef: ref
          }}
        >
          <CartItemList items={orderItems} type={type} />
        </ExpandableContainer>
        {showGiftNote && (
          <CartBreakdownItem
            LeftIcon={Gift}
            label={'Gift Note'}
            price={giftWrapping.price}
            children={<GiftNote>{giftWrapping.message}</GiftNote>}
            isCartLoading={isCartLoading}
            withLineBreak={false}
          />
        )}
      </Section>
      {!isEmpty(summary) && (
        <CartBreakdown
          summary={summary}
          loyaltyCards={loyaltyCards}
          fulfillmentType={fulfillmentType}
          isEditable={isEditable}
          isCartLoading={isCartLoading}
          {...(!!potentialLoyaltyGains && { potentialLoyaltyGains })}
        />
      )}
    </div>
  )
}

const Section = styled.div<StyledHTMLElement & { margin?: string }>(
  ({ margin = '24px 0' }) => ({
    margin: margin
  })
)

const NoteBreakdownItemContainer = styled.div(() => ({
  margin: '18px 0 32px'
}))

const GiftNote = styled.div(
  ({ theme }): CSSObject => ({
    backgroundColor: '#F5F5F5',
    padding: '12px',
    borderRadius: theme.borderRadius,
    marginBottom: '24px'
  })
)

export default Cart
